/**
 * Portfolio Configuration Modal
 * WP-ALLOC-CONFIG-1: Exposes TenantAllocationProfile settings
 *
 * Follows patterns from TemplateModal.tsx:
 * - Button default import (line 7)
 * - Mutation callback pattern (lines 29-43)
 * - Modal structure (lines 51-205)
 */
import { useEffect, useState } from 'react';
import Button from '@/components/ui/Button';
import { useProfile, useCreateProfile, useUpdateProfile } from '@/hooks/useAllocation';
import type { TenantProfileCreate, TenantProfileUpdate } from '@/types/allocation';

interface ConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ConfigurationModal({ isOpen, onClose }: ConfigurationModalProps) {
  // Query profile - returns null if not configured yet (404 is caught in hook)
  const { data: profile, isLoading } = useProfile();
  // Profile not found when data is null and we're done loading (404 returns null, not error)
  const profileNotFound = profile === null && !isLoading;

  // Mutations - callback pattern per TemplateModal.tsx lines 29-43
  const { mutate: createProfile, isPending: isCreating } = useCreateProfile();
  const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile();

  const isPending = isCreating || isUpdating;

  // Local form state
  const [rebalanceThreshold, setRebalanceThreshold] = useState(5);
  const [includeCash, setIncludeCash] = useState(true);
  const [includeRealEstate, setIncludeRealEstate] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Sync form state when profile loads OR modal opens
  useEffect(() => {
    if (isOpen && profile) {
      setRebalanceThreshold(profile.rebalanceThresholdPct);
      setIncludeCash(profile.includeCash);
      setIncludeRealEstate(profile.includeRealEstate);
      setHasChanges(false);
      setSaveError(null);
    }
  }, [profile, isOpen]);

  // Reset to defaults when opening modal with no profile
  useEffect(() => {
    if (isOpen && profileNotFound) {
      setRebalanceThreshold(5);
      setIncludeCash(true);
      setIncludeRealEstate(false);
      setHasChanges(false);
      setSaveError(null);
    }
  }, [profileNotFound, isOpen]);

  // Escape key handler - must use document listener for keyboard events
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Early return if not open
  if (!isOpen) return null;

  const handleSave = () => {
    const data: TenantProfileUpdate = {
      rebalanceThresholdPct: rebalanceThreshold,
      includeCash,
      includeRealEstate,
    };

    if (profileNotFound) {
      // Profile doesn't exist, create it
      createProfile(data as TenantProfileCreate, {
        onSuccess: () => {
          onClose();
        },
        onError: (err) => {
          setSaveError(err instanceof Error ? err.message : 'Failed to save settings');
        },
      });
    } else {
      // Update existing profile - callback pattern per TemplateModal.tsx
      updateProfile(data, {
        onSuccess: () => {
          onClose();
        },
        onError: (err) => {
          setSaveError(err instanceof Error ? err.message : 'Failed to save settings');
        },
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform bg-white rounded-lg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {profileNotFound ? 'Set Up Portfolio Settings' : 'Portfolio Settings'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <ConfigurationSkeleton />
            ) : (
              <div className="space-y-6">
                {/* Rebalance Threshold Section */}
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Rebalance Alerts</h3>
                    <p className="text-sm text-gray-500">
                      Notify when allocation drifts from target by:
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="25"
                      step="0.5"
                      value={rebalanceThreshold}
                      onChange={(e) => {
                        setRebalanceThreshold(parseFloat(e.target.value));
                        setHasChanges(true);
                      }}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      disabled={isPending}
                    />
                    <span className="w-12 text-right font-medium text-gray-900">
                      {rebalanceThreshold}%
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>1% (strict)</span>
                    <span>25% (relaxed)</span>
                  </div>
                </div>

                {/* Inclusion Settings Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900">Portfolio Inclusions</h3>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeCash}
                      onChange={(e) => {
                        setIncludeCash(e.target.checked);
                        setHasChanges(true);
                      }}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={isPending}
                    />
                    <div>
                      <div className="font-medium text-sm text-gray-900">Include Cash Holdings</div>
                      <div className="text-sm text-gray-500">
                        Count checking/savings balances as Cash asset class
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeRealEstate}
                      onChange={(e) => {
                        setIncludeRealEstate(e.target.checked);
                        setHasChanges(true);
                      }}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={isPending}
                    />
                    <div>
                      <div className="font-medium text-sm text-gray-900">Include Real Estate</div>
                      <div className="text-sm text-gray-500">
                        Count real estate account values in portfolio calculations
                      </div>
                    </div>
                  </label>
                </div>

                {/* Current Template Info (read-only) */}
                {profile?.template && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-900">Current Strategy</h3>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="font-medium text-gray-900">{profile.template.name}</div>
                      <div className="text-sm text-gray-500">
                        {profile.template.description}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error Message */}
          {saveError && (
            <div className="px-6 pb-2">
              <div className="text-sm text-red-600 bg-red-50 rounded-md p-3">
                {saveError}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button variant="ghost" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={(!profileNotFound && !hasChanges) || isPending || isLoading}
              isLoading={isPending}
            >
              {profileNotFound ? 'Set Up' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Loading skeleton - matches TemplateModal pattern
function ConfigurationSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="space-y-3">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="h-3 w-48 bg-gray-200 rounded" />
        <div className="h-6 w-full bg-gray-200 rounded" />
      </div>
      <div className="space-y-4">
        <div className="h-4 w-36 bg-gray-200 rounded" />
        <div className="h-14 w-full bg-gray-200 rounded" />
        <div className="h-14 w-full bg-gray-200 rounded" />
      </div>
    </div>
  );
}

// Icon components at bottom - matches AllocationPage.tsx pattern (lines 204-240)
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

// Exported for use in PageHeader button
export function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}
